import { ChangeEvent, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { useUploadCustomSpecList, useValidateCustomSpecList } from "@/hooks/useSpecLists";
import { FileDropZone } from "@/components/shared/FileDropZone";
import { InfoAlert, SuccessAlert, WarningAlert } from "@/components/shared/Alerts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DuplicateRow, ValidationResponse, ValidationTable } from "./ValidationTable";

type FormData = {
  csvFile?: File;
};

const DuplicateRowsFoundValidationComponent = ({ detail }: { detail: DuplicateRow }) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>report_name</TableHead>
            <TableHead>dut_name</TableHead>
            <TableHead>condition</TableHead>
            <TableHead>Nr of duplicates found</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          <TableRow>
            <TableCell>{detail.key.spec_name}</TableCell>
            <TableCell>{detail.key.report_name}</TableCell>
            <TableCell>{detail.key.dut_name}</TableCell>
            <TableCell>{detail.key.condition}</TableCell>
            <TableCell>{detail.rows.length}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

type CustomSpecListStepProps = {
  formData: FormData;
  isCreatingCustomSpecList: boolean;
  isValidatingCustomSpecList: boolean;
  onCreateValidatedCustomSpecList: () => void;
  onOpenChange: (open: boolean) => void;
  setChoice: (choice: "" | "derived" | "custom") => void;
  setFormData: (data: FormData) => void;
  setValidationResponse: (response: ValidationResponse | null) => void;
  validationResponse: ValidationResponse | null;
};

export const CustomSpecListStep = ({
  formData,
  isCreatingCustomSpecList,
  isValidatingCustomSpecList,
  onCreateValidatedCustomSpecList,
  onOpenChange,
  setChoice,
  setFormData,
  setValidationResponse,
  validationResponse,
}: CustomSpecListStepProps) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith(".csv")) {
      setFormData({ ...formData, csvFile: file });
    } else if (file) {
      toast.error("Please upload a CSV file");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith(".csv")) {
      setFormData({ ...formData, csvFile: file });
    } else if (file) {
      toast.error("Please upload a CSV file");
    }
  };

  const clearSelectedFile = () => {
    setFormData({ ...formData, csvFile: undefined });
  };

  const canSubmit = formData.csvFile;

  const retryFileUploadAfterValidationError = () => {
    clearSelectedFile();
    setValidationResponse(null);
  };

  const onBack = () => {
    setChoice("");
  };

  const onCancel = () => {
    onOpenChange(false);
  };

  if (validationResponse) {
    return (
      <div className="space-y-4 flex-1">
        {validationResponse.file_error?.type === "MissingColumnValidationError" ? (
          <WarningAlert>
            You're missing the following column(s) in your CSV file:
            {validationResponse.file_error.message}. Add the missing column(s) to the CSV file and
            try again.{" "}
          </WarningAlert>
        ) : null}
        {validationResponse.file_error?.type === "ExtraColumnFoundValidationError" ? (
          <WarningAlert>
            Your CSV file has extra column(s) defined that should not be there. Please remove the
            following column(s): {validationResponse.file_error.message} and try again.{" "}
          </WarningAlert>
        ) : null}
        {validationResponse.file_error?.type === "DuplicateRowsFoundValidationError" ? (
          <>
            <WarningAlert>Found duplicate rows for the following combination(s):</WarningAlert>
            {(validationResponse.file_error.duplicates ?? []).map((dupe, idx) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <DuplicateRowsFoundValidationComponent detail={dupe} key={`row-${idx}`} />
            ))}
          </>
        ) : null}
        {validationResponse.file_error?.type === "MaskSpecListNameConflict" ? (
          <WarningAlert>{validationResponse.file_error.message}</WarningAlert>
        ) : null}
        {!validationResponse.file_content_valid && validationResponse.file_error === null ? (
          <ValidationTable result={validationResponse} />
        ) : null}
        {validationResponse.file_content_valid ? (
          <>
            <SuccessAlert>
              CSV file content has been validated and no issues were found! Click "Create Custom
              Spec List" to create the custom spec list.
            </SuccessAlert>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="default"
                onClick={onCreateValidatedCustomSpecList}
                disabled={isCreatingCustomSpecList}
              >
                Create Custom Spec List
              </Button>
            </div>
          </>
        ) : (
          <>
            <InfoAlert>
              When in doubt, verify the documentation for custom spec lists.{" "}
              <a
                href="/docs/custom-spec-list-csv"
                className="text-primary underline font-semibold"
                target="_blank"
              >
                You can find the documentation here.
              </a>
            </InfoAlert>
            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={retryFileUploadAfterValidationError}>
                Clear selected file and retry file upload
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 flex-1">
      <FileDropZone
        acceptedFileFormats=".csv"
        clearSelectedFile={clearSelectedFile}
        documentationLink="/docs/custom-spec-list-csv"
        handleDrop={handleDrop}
        handleFileChange={handleFileChange}
        selectedFile={formData.csvFile}
      />

      <div className="flex justify-between gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isValidatingCustomSpecList}>
            Validate Custom Spec List
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Custom Flow Form
type CustomSpecListFormProps = {
  maskId: string;
  onOpenChange: (open: boolean) => void;
  refetchSpecListsData: () => void;
  setChoice: (choice: "" | "derived" | "custom") => void;
};

export const CustomSpecListForm = ({
  maskId,
  onOpenChange,
  refetchSpecListsData,
  setChoice,
}: CustomSpecListFormProps) => {
  const [validationResponse, setValidationResponse] = useState<ValidationResponse | null>(null);
  const { trigger: validationPreview, isMutating: isValidatingCustomSpecList } =
    useValidateCustomSpecList();
  const { trigger: createCustomSpecList, isMutating: isCreatingCustomSpecList } =
    useUploadCustomSpecList();

  const [formData, setFormData] = useState<FormData>({
    csvFile: undefined,
  });

  const handleSubmit = async (e: ChangeEvent) => {
    try {
      e.preventDefault();

      if (!formData.csvFile) return;

      setValidationResponse(null);

      await validationPreview(
        {
          mask_id: maskId,
          file: formData.csvFile,
          preview: true,
        },
        {
          onError: (error) => {
            toast.error(error.message);
          },
          onSuccess: (response) => {
            setValidationResponse(response.detail);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateValidatedCustomSpecList = async () => {
    try {
      if (!formData.csvFile) return;

      await createCustomSpecList(
        {
          mask_id: maskId,
          file: formData.csvFile,
          preview: false,
        },
        {
          onError: (error) => {
            toast.error(error.message);
          },
          onSuccess: () => {
            toast.success("Custom Spec List was successfully created!");
            refetchSpecListsData();
            onOpenChange(false);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <CustomSpecListStep
        formData={formData}
        isCreatingCustomSpecList={isCreatingCustomSpecList}
        isValidatingCustomSpecList={isValidatingCustomSpecList}
        onCreateValidatedCustomSpecList={handleCreateValidatedCustomSpecList}
        onOpenChange={onOpenChange}
        setChoice={setChoice}
        setFormData={setFormData}
        setValidationResponse={setValidationResponse}
        validationResponse={validationResponse}
      />
    </form>
  );
};
